import { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import './JobCard.scss';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ApplyModal from "./modals/ApplyModal";
import {
  faPencil,
  faLocationDot,
  faStar,
  faBookmark,
  faDesktop,
  faCaretDown,
  faCaretUp,
  faCheck,
  faXmark,
  faEllipsisVertical,
  faUser,
  faMessage
} from "@fortawesome/free-solid-svg-icons";
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useCurrentUser } from '../../UserProvider';
import classNames from 'classnames';
import ReactGA from "react-ga4";

export default function JobCard({ jobId, job: initialJob, afterRender, setJobToEdit, setJobs }) {
  const [job, setJob] = useState(initialJob || null);
  const [loading, setLoading] = useState(!initialJob);
  const [error, setError] = useState(false);
  const [displayApply, setDisplayApply] = useState(false);
  const [showApplicants, setShowApplicants] = useState(false);
  const [applicants, setApplicants] = useState(null);
  const { currentUser } = useCurrentUser();

  const isOwner = currentUser && job && currentUser.id === job?.user_id;

  const updateApplicationStatus = async (applicant, status) => {
    const prevStatus = applicant.status;
    setApplicants(prev =>
      prev.map(a => a.id === applicant.id ? { ...a, status } : a)
    );
    try {
      await api.put(`/users_job_postings/${applicant.id}`, { status });
    } catch (err) {
      console.error("Error updating application status:", err);
      setApplicants(prev =>
        prev.map(a => a.id === applicant.id ? { ...a, status: prevStatus } : a)
      );
    }
  };

  const toggleApplicants = async () => {
    if (!showApplicants && applicants === null) {
      const { data } = await api.get(`/users_job_postings`, { params: { job_posting_id: job.id, page: 1, per_page: 1000 } });
      setApplicants(data);
    }
    setShowApplicants(prev => !prev);
  };

  const toggleSaveJob = async (job) => {
    const isSaved = !!job.user_job_posting;
    const previousValue = job.user_job_posting;

    const optimisticValue = isSaved ? null : { optimistic: true };

    // Optimistic UI update
    if (setJobs) {
      setJobs(prev =>
        prev.map(j =>
          j.id === job.id
            ? { ...j, user_job_posting: optimisticValue }
            : j
        )
      );
    } else {
      setJob(prev => ({
        ...prev,
        user_job_posting: optimisticValue
      }));
    }

    try {
      if (!isSaved) {
        const { data } = await api.post(`/users_job_postings`, {
          job_posting_id: job.id,
          applied: false
        });

        if (setJobs) {
          setJobs(prev =>
            prev.map(j =>
              j.id === job.id
                ? { ...j, user_job_posting: data }
                : j
            )
          );
        } else {
          setJob(prev => ({
            ...prev,
            user_job_posting: data
          }));
        }

      } else {
        await api.delete(`/users_job_postings/${job.id}`);
      }

    } catch (err) {
      console.error("Error toggling saved job:", err);

      // Rollback
      if (setJobs) {
        setJobs(prev =>
          prev.map(j =>
            j.id === job.id
              ? { ...j, user_job_posting: previousValue }
              : j
          )
        );
      } else {
        setJob(prev => ({
          ...prev,
          user_job_posting: previousValue
        }));
      }
    }
  };

  const trackApplyClick = (job) => {
    let action = "Apply Click";
    if (job.is_recommended) {
      action = "Apply Click (rec)";
    }
    ReactGA.event({
      category: "Jobs",
      action: action,
      label: job.title,
      value: job.is_recommended ? 1 : 0,
      nonInteraction: false,
    });

    ReactGA.send({
      hitType: "event",
      eventCategory: "Jobs",
      eventAction: action,
      eventLabel: job.title,
      recommended: job.is_recommended,
      job_id: job.id,
    });
  };

  useEffect(() => {
    if (initialJob) {
      setJob(initialJob);
      setLoading(false);
      return;
    }

    if (!jobId) return;

    const fetchJob = async () => {
      try {
        const response = await api.get(`/job_postings/${jobId}`);
        setJob(response.data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [jobId, initialJob]);

  useEffect(() => {
    if (!loading && job && afterRender) {
      afterRender(job);
    }
  }, [loading, job, afterRender]);

  if (loading) {
    return (
      <div className="__job-card">
        Loading...
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="__job-card">
        Failed to load job.
      </div>
    );
  }

  return (
    <div className='job-card-content'>
      <div key={job.id} className={ classNames("job-card", "glass", { "recommended": job.is_recommended }) }>
        {job.is_recommended &&
          <div className='papaya-recommends'>
            <span><FontAwesomeIcon icon={faStar} /> Papaya Recommends</span>
          </div>
        }
        <div className='job-header'>
          <Link to={`/job/${job.id}`} className='job-title'>
            {job.title}
          </Link>
          <div className='right-controls'>
            {currentUser?.is_admin &&
              <button className='edit ppy-btn ppy-btn-primary' data-cta="apply-recommended" onClick={() => setJobToEdit(job)}>
                <FontAwesomeIcon icon={faPencil} />
              </button>
            }
            {(!job.user_job_posting || !job.user_job_posting.applied) && currentUser && currentUser.id !== job.user_id && (
              <button
                className={classNames(
                  "edit",
                  "ppy-btn",
                  { 'ppy-btn-secondary': !job.user_job_posting },
                  { 'ppy-btn-primary': job.user_job_posting }
                )}
                data-cta="save-job"
                onClick={() => toggleSaveJob(job)}
              >
                <FontAwesomeIcon icon={faBookmark} />
              </button>
            )}
          </div>
        </div>

        {job.tags && job.tags.trim() !== '' && (
          <div className='tags'>
            {job.tags.split(',').map((tag, idx) => (
              <div key={idx} className='tag'>
                {tag.trim()}
              </div>
            ))}
          </div>
        )}

        {!!job.budget && job.budget !== 0 && (
          <div className='budget'>Budget: ${job.budget}</div>
        )}

        <div className='body'>{job.description}</div>
        {(job.remote || (job.location && job.location.trim() !== "")) && (
          <div className='location'>
            <FontAwesomeIcon icon={job.remote ? faDesktop : faLocationDot} />{" "}
            {job.remote ? "Remote" : job.location}
          </div>
        )}
        {(!job.user_job_posting || !job.user_job_posting.applied) && (!currentUser || currentUser.id !== job.user_id) && (
          <div className='apply'>
            <button className="ppy-btn ppy-btn-primary" data-cta="apply-job" onClick={() => {trackApplyClick(job); setDisplayApply(true);}}>
              Apply Now
            </button>
          </div>
        )}
        <div className="dates">
          <div>Posted {new Date(job.created_at).toLocaleDateString('en-US')}</div>
          { job.expires_at &&
            <div className="expires-at">
              <div>Expires {new Date(job.expires_at).toLocaleDateString('en-US')}</div>
              {new Date(job.expires_at) < new Date() && (
                <div className="expired">Expired</div>
              )}
            </div>
          }
        </div>
        {currentUser?.is_admin  &&
          <div className="metadata">
            <div>Owner Reached: {job.owner_reached ? 'Yes' : 'No'}</div>
          </div>
        }
        {(currentUser?.is_admin || isOwner) &&
          <div className="metadata">
            <div>Applications: {job.application_count}</div>
          </div>
        }

        <ApplyModal
          show={displayApply}
          handleClose={() => setDisplayApply(false)}
          job={job}
        />
        {isOwner && (
          <div className="applicants-toggle">
              <FontAwesomeIcon onClick={toggleApplicants} icon={showApplicants ? faCaretUp : faCaretDown} />
          </div>
        )}

        {isOwner && showApplicants && applicants?.length > 0 && (
          <div className="applicants">
            <div className="applicants-list">
              {applicants.map((applicant) => (
                <div key={applicant.id} className="applicant">
                  <div className='applicant-header'>
                    <a href={`/p/${applicant.display_name}`} target="_blank" rel="noopener noreferrer">
                      {applicant.name}
                    </a>
                    <div className="applicant-status-buttons">
                      <button
                        className={classNames("accept", { active: applicant.status === "accepted" })}
                        onClick={() => updateApplicationStatus(applicant, "accepted")}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                      <button
                        className={classNames("reject", { active: applicant.status === "rejected" })}
                        onClick={() => updateApplicationStatus(applicant, "rejected")}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                      <Dropdown align="end">
                        <Dropdown.Toggle as="div" bsPrefix="custom-toggle" className="applicant-menu-toggle">
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="dropdown__menu">
                          <Dropdown.Item as="a" href={`/p/${applicant.display_name}`} target="_blank" rel="noopener noreferrer" className="dropdown__item">
                            <FontAwesomeIcon icon={faUser} /> Check Profile
                          </Dropdown.Item>
                          <Dropdown.Item as="a" href={`/inbox/${applicant.display_name}`} target="_blank" rel="noopener noreferrer" className="dropdown__item">
                            <FontAwesomeIcon icon={faMessage} /> Message
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                  <div className="applicant-details">
                    {applicant.details}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}